import {
  Box,
  Collapse,
  HStack,
  Icon,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import type {
  CampaignIntent,
  DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import type { ActionableCampaignIntent } from "common/interfaces/CampaignIntent";
import CampaignIntentsBlock, {
  MAX_DISPLAYED_INTENTS,
  sortIntentsByImportance,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CampaignIntentsBlock";

/** Do not show DISABLED (superseded) or IGNORED (user dismissed) intents. */
const visibleIntents = (intents: CampaignIntent[]) =>
  (intents ?? []).filter(
    (i) =>
      (i.status as string) !== "DISABLED" &&
      (i.status as string) !== "IGNORED"
  );

const actionableIntents = (intents: CampaignIntent[]) =>
  intents.filter((i) => i.actionable === true);

export default function PerformanceInsights({
  campaignId,
  intents,
  campaign,
  onApplied,
  onScrollToAdset,
  pollPublishAfterIntentApply,
}: {
  campaignId: string;
  intents: CampaignIntent[];
  campaign?: DisplayMarketingCampaign;
  onApplied: () => void | Promise<void>;
  /** When set, clicking an insight scrolls the page to the corresponding ad set card. */
  onScrollToAdset?: (adsetId: string) => void;
  pollPublishAfterIntentApply?: (p: {
    campaign_id: string;
    update_id: string;
    intent: ActionableCampaignIntent;
  }) => Promise<void>;
}) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const visible = visibleIntents(intents);
  const actionable = actionableIntents(visible);
  const toShow = sortIntentsByImportance(actionable).slice(
    0,
    MAX_DISPLAYED_INTENTS
  );
  const count = toShow.length;
  if (count === 0) return null;

  return (
    <Box
      bg="#F5F5F5"
      borderRadius="2xl"
      boxShadow="0 2px 12px rgba(0,0,0,0.08)"
      borderWidth="1px"
      borderColor="gray.100"
      overflow="hidden"
      gap={0}
    >
      <HStack
        justify="space-between"
        px={5}
        py={4}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: "gray.50" }}
        transition="background 0.15s"
      >
        <HStack gap={2}>
          <Box
            borderRadius="full"
            bg="#2C3E50"
            color="white"
            fontSize="md"
            fontWeight="600"
            minW={7}
            h={7}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {count}
          </Box>
          <Text
            fontFamily="Work Sans"
            fontWeight="600"
            fontSize="24px"
            lineHeight="120%"
            letterSpacing="-3%"
            verticalAlign="middle"
            color="#2C3E50"
          >
            Performance Insights
          </Text>
        </HStack>
        <IconButton
          aria-label={isOpen ? "Collapse" : "Expand"}
          icon={
            <Icon
              as={isOpen ? ChevronUpIcon : ChevronDownIcon}
              boxSize={8}
              borderWidth={0.5}
            />
          }
          variant="outline"
          size="sm"
          borderRadius="full"
          borderColor="gray.300"
          color="gray.400"
          bg="white"
          _hover={{ bg: "gray.50", borderColor: "gray.400" }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
      <Collapse in={isOpen}>
        <Box
          borderRadius="xl"
          bg="white"
          boxShadow="0px 4px 8px 0px #0000000D"
          borderWidth="1px"
          borderColor="gray.200"
          m={2}
          mt={0}
        >
          <CampaignIntentsBlock
            campaignId={campaignId}
            intents={toShow}
            campaign={campaign}
            onApplied={onApplied}
            onScrollToAdset={onScrollToAdset}
            pollPublishAfterIntentApply={pollPublishAfterIntentApply}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
